class LBSearchDrawer {
  constructor() {
    if (window.innerWidth < 990) return;

    this.modal = document.querySelector('#search-modal');
    this.dialog = this.modal?.querySelector('dialog');
    this.inner = this.dialog?.querySelector('.lb-search-drawer__inner');

    this.recentMount = document.querySelector('[data-lb-search-recent-list]');
    this.recentBlock = document.querySelector('[data-lb-search-recent]');

    this.resultsMount = document.querySelector('[data-lb-search-results-list]');
    this.resultsBlock = document.querySelector('[data-lb-search-results]');

    this.maxProducts = Number(this.recentMount?.dataset.maxProducts || 8);

    if (!this.modal || !this.dialog || !this.inner || !this.recentMount) return;

    this.overlay = null;
    this.heightRaf = null;
    this.searchTimer = null;
    this.lastQuery = '';
    this.savedScrollY = 0;

    this.init();
  }

  init() {
    this.interceptSearchTriggers();
    this.bindEvents();
    this.loadRecentProducts();
  }

  getScrollY() {
    return window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
  }

  interceptSearchTriggers() {
    document.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('[data-lb-search-open]');

        if (!trigger) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.openSmoothly();
      },
      true
    );
  }

  createOverlay() {
    document.querySelectorAll('[data-lb-search-overlay]').forEach((el) => {
      el.remove();
    });

    const overlay = document.createElement('button');
    overlay.type = 'button';
    overlay.className = 'lb-search-page-overlay';
    overlay.setAttribute('data-lb-search-overlay', '');
    overlay.setAttribute('aria-label', 'Close search');

    document.body.appendChild(overlay);

    this.overlay = overlay;

    this.overlay.addEventListener('click', () => {
      this.closeSmoothly();
    });
  }

  openSmoothly() {
    if (this.dialog.open) return;

    this.savedScrollY = this.getScrollY();

    this.createOverlay();

    this.dialog.classList.remove('lb-search-closing');
    this.dialog.classList.add('lb-search-ready');

    document.documentElement.classList.add('lb-search-animating');

    /*
      Important:
      Do NOT use native showModal().
      Native dialog/showModal can force page scroll to top.
    */
    this.dialog.setAttribute('open', '');

    this.updateSearchMode();
    this.syncDrawerHeight();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.overlay?.classList.add('is-visible');
        this.dialog.classList.add('lb-search-visible');
        this.syncDrawerHeight();

        window.setTimeout(() => {
          document.documentElement.classList.remove('lb-search-animating');
          this.syncDrawerHeight();
        }, 800);
      });
    });

    this.bindInputOnce();
  }

  bindInputOnce() {
    const input = this.getInput();

    if (!input || input.dataset.lbSearchBound === 'true') return;

    input.dataset.lbSearchBound = 'true';

    input.addEventListener('input', () => {
      this.updateSearchMode();

      window.setTimeout(() => this.syncDrawerHeight(), 50);
      window.setTimeout(() => this.syncDrawerHeight(), 180);
      window.setTimeout(() => this.syncDrawerHeight(), 420);
    });

    input.addEventListener('search', () => {
      this.forceUpdateFromInput();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (input.value.trim()) {
          event.preventDefault();
          this.clearSearch();
        } else {
          event.preventDefault();
          this.closeSmoothly();
        }
      }
    });
  }

  bindEvents() {
    this.dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.closeSmoothly();
      },
      true
    );

    this.dialog.addEventListener(
      'click',
      (event) => {
        const forceClose = event.target.closest('[data-lb-search-force-close]');
        const forceClear = event.target.closest('[data-lb-search-clear]');

        const nativeClose = event.target.closest(
          [
            '.predictive-search__close-modal-button',
            '[data-close]',
            '[data-dialog-close]',
            'button[aria-label="Close"]',
            'button[aria-label="Close search"]',
            'button[aria-label*="Close"]',
            'button[aria-label*="close"]'
          ].join(',')
        );

        const resetButton = event.target.closest(
          [
            '.predictive-search__reset-button',
            'button[type="reset"]',
            '[aria-label*="Clear"]',
            '[aria-label*="clear"]',
            '[aria-label*="Reset"]',
            '[aria-label*="reset"]'
          ].join(',')
        );

        const cancelButton = event.target.closest('button, a');

        if (forceClear) {
          event.preventDefault();
          event.stopPropagation();
          this.clearSearch();
          return;
        }

        if (forceClose) {
          event.preventDefault();
          event.stopPropagation();
          this.closeSmoothly();
          return;
        }

        if (resetButton) {
          event.preventDefault();
          event.stopPropagation();
          this.clearSearch();
          return;
        }

        if (cancelButton && /cancel/i.test(cancelButton.textContent || '')) {
          event.preventDefault();
          event.stopPropagation();
          this.clearSearch();
          return;
        }

        if (nativeClose) {
          event.preventDefault();
          event.stopPropagation();
          this.closeSmoothly();
        }
      },
      true
    );

    const observer = new MutationObserver(() => {
      this.forceUpdateFromInput();
      this.scheduleHeightSync();
    });

    observer.observe(this.inner, {
      childList: true,
      subtree: true,
      attributes: true
    });

    window.addEventListener('resize', () => {
      this.scheduleHeightSync();
    });
  }

  getInput() {
    return this.dialog.querySelector('input[type="search"], input[name="q"]');
  }

  forceUpdateFromInput() {
    requestAnimationFrame(() => {
      this.updateSearchMode();
    });
  }

  clearSearch() {
    const input = this.getInput();

    if (input) {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.lastQuery = '';

    if (this.resultsBlock) {
      this.resultsBlock.setAttribute('hidden', '');
    }

    if (this.resultsMount) {
      this.resultsMount.innerHTML = '';
    }

    this.dialog.classList.add('lb-search-empty');
    this.dialog.classList.remove('lb-search-has-query');

    this.scheduleHeightSync();
  }

  updateSearchMode() {
    const input = this.getInput();
    const value = input?.value?.trim() || '';

    if (value === '') {
      this.dialog.classList.add('lb-search-empty');
      this.dialog.classList.remove('lb-search-has-query');

      this.lastQuery = '';

      if (this.resultsBlock) {
        this.resultsBlock.setAttribute('hidden', '');
      }

      if (this.resultsMount) {
        this.resultsMount.innerHTML = '';
      }
    } else {
      this.dialog.classList.remove('lb-search-empty');
      this.dialog.classList.add('lb-search-has-query');

      this.queueSearchProducts(value);
    }

    this.scheduleHeightSync();
  }

  queueSearchProducts(query) {
    clearTimeout(this.searchTimer);

    this.searchTimer = window.setTimeout(() => {
      this.loadSearchProducts(query);
    }, 220);
  }

  async loadSearchProducts(query) {
    if (!this.resultsMount || !this.resultsBlock) return;

    const cleanQuery = String(query || '').trim();

    if (!cleanQuery || cleanQuery === this.lastQuery) return;

    this.lastQuery = cleanQuery;

    this.resultsBlock.removeAttribute('hidden');
    this.resultsMount.innerHTML = `
      <div class="lb-search-recent__empty">
        Loading products...
      </div>
    `;

    try {
      const suggestUrl = new URL('/search/suggest.json', window.location.origin);
      suggestUrl.searchParams.set('q', cleanQuery);
      suggestUrl.searchParams.set('resources[type]', 'product');
      suggestUrl.searchParams.set(
        'resources[limit]',
        String(Number(this.resultsMount.dataset.maxProducts || 8))
      );
      suggestUrl.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(suggestUrl.toString(), {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        this.showEmptySearchResults();
        return;
      }

      const data = await response.json();
      const products = data?.resources?.results?.products || [];

      if (!products.length) {
        this.showEmptySearchResults();
        return;
      }

      const urls = products
        .map((product) => this.normalizeUrl(product.url))
        .filter(Boolean);

      await this.renderCollectionCards({
        urls,
        mount: this.resultsMount,
        collectionUrl: this.resultsMount.dataset.collectionUrl || '/collections/all',
        maxPages: Number(this.resultsMount.dataset.maxPages || 8),
        sectionId: this.resultsMount.dataset.sectionId || '',
        emptyText: 'No products found.'
      });

      this.syncDrawerHeight();
    } catch (error) {
      console.warn('[LB Search] custom search results failed:', error);
      this.showEmptySearchResults();
    }
  }

  async loadRecentProducts() {
    const recent = window.LBRecentlyViewed?.get?.(this.maxProducts) || [];

    if (!recent.length) {
      this.showEmptyRecent();
      return;
    }

    const urls = recent
      .map((item) => this.normalizeUrl(item.url))
      .filter(Boolean);

    await this.renderCollectionCards({
      urls,
      mount: this.recentMount,
      collectionUrl: this.recentMount.dataset.collectionUrl || '/collections/all',
      maxPages: Number(this.recentMount.dataset.maxPages || 8),
      sectionId: this.recentMount.dataset.sectionId || '',
      emptyText: 'Recently viewed products will appear here.'
    });

    this.dialog.classList.add('lb-recent-ready');
    this.recentBlock?.classList.add('is-loaded');

    window.setTimeout(() => this.syncDrawerHeight(), 50);
    window.setTimeout(() => this.syncDrawerHeight(), 250);
    window.setTimeout(() => this.syncDrawerHeight(), 600);
  }

  async renderCollectionCards({ urls, mount, collectionUrl, maxPages, sectionId, emptyText }) {
    const wantedUrls = urls.map((url) => this.normalizeUrl(url));
    const foundCards = new Map();

    let templateGrid = null;

    for (let page = 1; page <= maxPages; page++) {
      if (foundCards.size >= wantedUrls.length && templateGrid) break;

      const urlsToTry = [];

      if (sectionId) {
        const sectionUrl = new URL(collectionUrl, window.location.origin);
        sectionUrl.searchParams.set('page', page);
        sectionUrl.searchParams.set('section_id', sectionId);
        urlsToTry.push(sectionUrl.toString());
      }

      const fullUrl = new URL(collectionUrl, window.location.origin);
      fullUrl.searchParams.set('page', page);
      urlsToTry.push(fullUrl.toString());

      for (const fetchUrl of urlsToTry) {
        if (foundCards.size >= wantedUrls.length && templateGrid) break;

        const response = await fetch(fetchUrl, {
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) continue;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const grid =
          doc.querySelector('.resource-list') ||
          doc.querySelector('.product-grid') ||
          doc.querySelector('ul:has(product-card)') ||
          doc.querySelector('ul:has(.product-card)');

        if (grid && !templateGrid) {
          templateGrid = grid.cloneNode(false);
        }

        const cards = [
          ...doc.querySelectorAll(
            [
              '.resource-list__item',
              '.product-grid__item',
              'li:has(product-card)',
              'li:has(.product-card)'
            ].join(',')
          )
        ];

        cards.forEach((card) => {
          const link = card.querySelector('a[href*="/products/"]');
          if (!link) return;

          const cardUrl = this.normalizeUrl(link.getAttribute('href'));

          if (wantedUrls.includes(cardUrl) && !foundCards.has(cardUrl)) {
            const clone = card.cloneNode(true);
            this.cleanCard(clone);
            foundCards.set(cardUrl, clone);
          }
        });
      }
    }

    if (!foundCards.size) {
      mount.innerHTML = `
        <div class="lb-search-recent__empty">
          ${emptyText}
        </div>
      `;
      return;
    }

    const grid = templateGrid || document.createElement('div');

    grid.classList.add('lb-search-recent__collection-grid');
    grid.removeAttribute('id');
    grid.innerHTML = '';

    wantedUrls.forEach((url) => {
      const card = foundCards.get(url);
      if (!card) return;
      grid.appendChild(card);
    });

    mount.innerHTML = '';
    mount.appendChild(grid);
  }

  showEmptyRecent() {
    this.recentMount.innerHTML = `
      <div class="lb-search-recent__empty">
        Recently viewed products will appear here.
      </div>
    `;

    this.dialog.classList.add('lb-recent-ready');
    this.recentBlock?.classList.add('is-loaded');
    this.syncDrawerHeight();
  }

  showEmptySearchResults() {
    if (!this.resultsMount || !this.resultsBlock) return;

    this.resultsBlock.removeAttribute('hidden');
    this.resultsMount.innerHTML = `
      <div class="lb-search-recent__empty">
        No products found.
      </div>
    `;

    this.syncDrawerHeight();
  }

  cleanCard(card) {
    card.querySelectorAll('script').forEach((script) => script.remove());

    card
      .querySelectorAll(
        [
          '.quick-add',
          '.quick-add__button',
          '.product-card__quick-add',
          '.product-card__buttons'
        ].join(',')
      )
      .forEach((el) => el.remove());

    const uid = `lb-search-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const idMap = new Map();

    card.querySelectorAll('[id]').forEach((el) => {
      const oldId = el.id;
      const newId = `${oldId}-${uid}`;

      idMap.set(oldId, newId);
      el.id = newId;
    });

    const attrsToUpdate = [
      'for',
      'aria-controls',
      'aria-labelledby',
      'aria-describedby',
      'data-controls',
      'data-target'
    ];

    card.querySelectorAll('*').forEach((el) => {
      attrsToUpdate.forEach((attr) => {
        const value = el.getAttribute(attr);
        if (!value) return;

        const updated = value
          .split(' ')
          .map((part) => idMap.get(part) || part)
          .join(' ');

        el.setAttribute(attr, updated);
      });

      const href = el.getAttribute('href');

      if (href && href.startsWith('#')) {
        const targetId = href.slice(1);

        if (idMap.has(targetId)) {
          el.setAttribute('href', `#${idMap.get(targetId)}`);
        }
      }
    });

    card.querySelectorAll('img').forEach((img) => {
      img.setAttribute('loading', 'eager');
      img.setAttribute('decoding', 'async');
    });
  }

  scheduleHeightSync() {
    if (this.heightRaf) cancelAnimationFrame(this.heightRaf);

    this.heightRaf = requestAnimationFrame(() => {
      this.syncDrawerHeight();
    });
  }

  syncDrawerHeight() {
    if (!this.dialog || !this.inner) return;

    const viewportLimit = Math.max(window.innerHeight - 22, 420);
    const contentHeight = Math.ceil(this.inner.scrollHeight);
    const finalHeight = Math.min(contentHeight, viewportLimit);

    this.dialog.style.setProperty('--lb-search-drawer-height', `${finalHeight}px`);
    this.dialog.style.setProperty('--lb-search-drawer-max-height', `${viewportLimit}px`);
  }

  closeSmoothly() {
    if (!this.dialog.open) return;
    if (this.dialog.classList.contains('lb-search-closing')) return;

    const y = this.savedScrollY || this.getScrollY();

    document.documentElement.classList.add('lb-search-animating');

    this.overlay?.classList.remove('is-visible');
    this.dialog.classList.remove('lb-search-visible');
    this.dialog.classList.add('lb-search-closing');

    window.setTimeout(() => {
      this.resetState(y);

      window.setTimeout(() => {
        this.resetState(y);
      }, 80);
    }, 620);
  }

  resetState(savedY) {
    const y = typeof savedY === 'number' ? savedY : this.savedScrollY || 0;

    document.documentElement.classList.remove(
      'lb-search-open',
      'lb-search-animating'
    );

    document.body.classList.remove(
      'lb-search-open',
      'lb-search-animating'
    );

    document.documentElement.style.overflow = '';
    document.documentElement.style.overflowY = '';
    document.documentElement.style.position = '';
    document.documentElement.style.height = '';
    document.documentElement.style.width = '';

    document.body.style.overflow = '';
    document.body.style.overflowY = '';
    document.body.style.position = '';
    document.body.style.height = '';
    document.body.style.width = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';

    this.overlay?.classList.remove('is-visible');
    this.overlay?.remove();
    this.overlay = null;

    document.querySelectorAll('[data-lb-search-overlay]').forEach((el) => {
      el.remove();
    });

    this.dialog.removeAttribute('open');

    this.dialog.classList.remove(
      'lb-search-ready',
      'lb-search-visible',
      'lb-search-closing',
      'lb-search-empty',
      'lb-search-has-query'
    );

    this.dialog.style.removeProperty('--lb-search-drawer-height');
    this.dialog.style.removeProperty('--lb-search-drawer-max-height');

    document.querySelectorAll('[inert]').forEach((el) => {
      el.removeAttribute('inert');
    });

    document.querySelectorAll('[aria-hidden="true"]').forEach((el) => {
      if (!el.closest('#search-modal')) {
        el.removeAttribute('aria-hidden');
      }
    });

    window.scrollTo({
      top: y,
      left: 0,
      behavior: 'auto'
    });

    requestAnimationFrame(() => {
      window.scrollTo({
        top: y,
        left: 0,
        behavior: 'auto'
      });
    });
  }

  normalizeUrl(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
    } catch {
      return String(url || '').split('?')[0].replace(/\/$/, '');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new LBSearchDrawer();
});